<div class="p-4">
  <mat-card class="p-6">
    @if (selectedQuoteLoading()) {
      <div class="flex justify-center p-4">
        <mat-spinner></mat-spinner>
      </div>
    } @else if (selectedQuoteError()) {
      <div class="flex justify-center p-4">
        <p class="text-red-500">{{ CONFIG.ERROR_MESSAGES.DEFAULT }}</p>
      </div>
    } @else {
      <mat-card-header>
        <mat-card-title class="pl-0 text-xl font-semibold pb-4">
          {{ isEditMode() ? 'Edit Quote' : 'Add New Quote' }}
          {{ isEditMode() ? '# ' + selectedQuote()?.quoteId : '' }}
        </mat-card-title>
      </mat-card-header>

      <mat-card-content>
        @if (isEditMode()) {
          <div class="grid grid-cols-2 gap-8 text-left">
            <div>
              <p class="pb-2">
                <span class="font-medium">Created By</span>:
                {{ selectedQuote()?.createdBy?.firstName }}
                {{ selectedQuote()?.createdBy?.lastName }}
              </p>
              <p class="pb-2">
                <span class="font-medium">Created On</span>:
                {{ selectedQuote()?.dateCreated | date: CONFIG.date.createdDateFormat }}
              </p>
            </div>
            <div>
              <div class="pb-2">
                <span class="font-medium">Client Company</span>:
                {{ selectedQuote()?.client?.company }}
              </div>
              <div class="pb-2">
                <span class="font-medium">Customer</span>: {{ selectedQuote()?.client?.code }}
              </div>
              <div class="pb-2">
                <span class="font-medium">Email</span>:
                {{ selectedQuote()?.client?.email }}
              </div>
              <div class="pb-2">
                <span class="font-medium">Phone</span>:
                {{ selectedQuote()?.client?.phone }}
              </div>
            </div>
          </div>
        }

        <form [formGroup]="quoteForm" (ngSubmit)="onSubmit()" class="mt-4">
          <!-- Original Form Fields -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <mat-form-field class="w-full">
              <mat-label>Est. Ship Date</mat-label>
              <input matInput type="text" formControlName="estShipDate" />
            </mat-form-field>

            <mat-form-field class="w-full">
              <mat-label>Choose Client</mat-label>
              <input
                type="text"
                matInput
                formControlName="client"
                [matAutocomplete]="auto"
                (click)="quoteForm.get('client')?.setValue(quoteForm.get('client')?.value || '')"
                required
              />
              <mat-autocomplete #auto="matAutocomplete" [displayWith]="displayFn">
                @for (client of filteredOptions$ | async; track client) {
                  <mat-option [value]="client">
                    {{ client.firstName }} {{ client.lastName }}, #{{ client.customerCode }},
                    {{ client.company }},
                    {{ client.customerState }}
                  </mat-option>
                }
              </mat-autocomplete>
              @if (
                quoteForm.get('client')?.hasError('required') && quoteForm.get('client')?.touched
              ) {
                <mat-error>Client is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field class="w-full">
              <mat-label>Choose Assignee</mat-label>
              <mat-select formControlName="assignee" required>
                @for (assignee of assignees(); track assignee.id) {
                  <mat-option [value]="assignee">
                    {{ assignee.firstName }} {{ assignee.lastName }}
                  </mat-option>
                }
              </mat-select>
              @if (
                quoteForm.get('assignee')?.hasError('required') &&
                quoteForm.get('assignee')?.touched
              ) {
                <mat-error>Assignee is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field class="w-full">
              <mat-label>Choose Status</mat-label>
              <mat-select formControlName="status" required>
                @for (option of statuses(); track option.key) {
                  @if (option.key !== 'IN_PRODUCTION' || isEditMode()) {
                    <mat-option [value]="option.key">{{ option.value }}</mat-option>
                  } @else {
                    <mat-option [value]="option.key" disabled>{{ option.value }}</mat-option>
                  }
                }
              </mat-select>
              @if (
                quoteForm.get('status')?.hasError('required') && quoteForm.get('status')?.touched
              ) {
                <mat-error>Status is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field class="w-full">
              <mat-label>Feet Per Reel</mat-label>
              <input matInput type="text" formControlName="feetPerReel" required />
              @if (
                quoteForm.get('feetPerReel')?.hasError('required') &&
                quoteForm.get('feetPerReel')?.touched
              ) {
                <mat-error>Feet Per Reel is required</mat-error>
              }
            </mat-form-field>

            <mat-form-field class="w-full">
              <mat-label>Shipping Method</mat-label>
              <mat-select formControlName="shippingMethod">
                @for (method of quotesShippingMethod(); track method.key) {
                  <mat-option [value]="method.key">{{ method.value }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <mat-checkbox formControlName="certsRequired">Certs Required</mat-checkbox>
            <mat-checkbox formControlName="testReports">Test Reports</mat-checkbox>
            <mat-checkbox formControlName="cfm">CFM</mat-checkbox>
            <mat-checkbox formControlName="ciwToSupplyWire">CIW To supply wire</mat-checkbox>
          </div>

          <ciwc-upload-media
            [attachment]="attachments"
            [parentForm]="quoteForm"
          ></ciwc-upload-media>

          <mat-form-field class="w-full mb-8">
            <mat-label>Quote Description</mat-label>
            <textarea matInput formControlName="description" rows="4"></textarea>
          </mat-form-field>

          <div class="grid grid-cols-2 gap-6 mb-2">
            <div class="bg-blue-500 text-white p-3 mb-2 h-12">
              <h2 class="text-lg font-medium">Quantity Details</h2>
            </div>

            <div class="bg-blue-500 p-1 mb-2">
              <mat-radio-group
                formControlName="quoteType"
                class="quote-type-radio-group flex items-center"
              >
                <mat-radio-button value="caNumber" class="mr-4">
                  Quote by CA Number
                </mat-radio-button>
                <mat-radio-button value="customQuote"> Custom Quote </mat-radio-button>
              </mat-radio-group>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-6 mb-8 h-full">
            <div class="p-4 pt-0 border border-gray-200 h-full">
              <mat-form-field class="w-full mb-4">
                <mat-label>Quantity</mat-label>
                <input matInput formControlName="quantity" />
              </mat-form-field>

              <div class="mb-4">
                <mat-checkbox formControlName="breakQuoteIntoEach">
                  Break quote into each
                </mat-checkbox>
              </div>

              @if (quoteForm.get('breakQuoteIntoEach')?.value) {
                <div formArrayName="quantityBreaks">
                  @for (break of quantityBreaksArray.controls; track break; let i = $index) {
                    <div
                      [formGroupName]="i"
                      class="grid grid-cols-[1fr_1fr_auto] items-center gap-4 mb-4"
                    >
                      <mat-form-field class="w-full">
                        <mat-label>Quantity Break {{ i + 1 }}*</mat-label>
                        <input matInput type="number" formControlName="quantity" required />
                        @if (
                          break.get('quantity')?.hasError('required') &&
                          break.get('quantity')?.touched
                        ) {
                          <mat-error>Quantity is required</mat-error>
                        }
                      </mat-form-field>

                      <mat-form-field class="w-full">
                        <mat-label>Pieces</mat-label>
                        <input matInput type="number" formControlName="pieces" required />
                        @if (
                          break.get('pieces')?.hasError('required') && break.get('pieces')?.touched
                        ) {
                          <mat-error>Pieces is required</mat-error>
                        }
                      </mat-form-field>

                      @if (i > 0 || quantityBreaksArray.length > 1) {
                        <button
                          mat-icon-button
                          color="warn"
                          type="button"
                          class="absolute right-0"
                          (click)="removeQuantityBreak(i)"
                        >
                          <mat-icon>delete</mat-icon>
                        </button>
                      }
                    </div>
                  }
                </div>
              } @else {
                <div formArrayName="quantityBreaks" class="flex flex-col gap-4">
                  @for (break of quantityBreaksArray.controls; track break; let i = $index) {
                    <div [formGroupName]="i" class="grid grid-cols-[1fr_auto] items-center gap-4">
                      <mat-form-field class="w-full">
                        <mat-label>Quantity Break {{ i + 1 }}*</mat-label>
                        <input matInput type="number" formControlName="quantity" required />
                        @if (
                          break.get('quantity')?.hasError('required') &&
                          break.get('quantity')?.touched
                        ) {
                          <mat-error>Quantity is required</mat-error>
                        }
                      </mat-form-field>

                      @if (i > 0 || quantityBreaksArray.length > 1) {
                        <button
                          mat-icon-button
                          color="warn"
                          type="button"
                          class="absolute right-0"
                          (click)="removeQuantityBreak(i)"
                        >
                          <mat-icon>delete</mat-icon>
                        </button>
                      }
                    </div>
                  }
                </div>
              }

              <div class="flex justify-end">
                <button
                  mat-mini-fab
                  color="primary"
                  type="button"
                  class="mt-2"
                  (click)="addQuantityBreak()"
                >
                  <mat-icon>add</mat-icon>
                </button>
              </div>
            </div>

            <div class="p-4 pt-0 border border-gray-200 h-full flex flex-col justify-between">
              @if (quoteForm.get('quoteType')?.value === 'customQuote') {
                <mat-form-field class="w-full">
                  <mat-label>Custom Quote Description</mat-label>
                  <textarea matInput formControlName="caCustomDescription" rows="4"></textarea>
                </mat-form-field>
              } @else {
                <mat-form-field class="w-full">
                  <mat-label>CA Number</mat-label>
                  <input
                    type="text"
                    matInput
                    formControlName="caNumber"
                    [matAutocomplete]="caAuto"
                    (click)="
                      quoteForm.get('caNumber')?.setValue(quoteForm.get('caNumber')?.value || '')
                    "
                  />
                  <mat-autocomplete #caAuto="matAutocomplete" [displayWith]="displayCANumber">
                    @for (caNumber of filteredCANumbers$ | async; track caNumber.id) {
                      <mat-option [value]="caNumber">{{ caNumber.caNumber }}</mat-option>
                    }
                  </mat-autocomplete>
                </mat-form-field>
              }

              <div class="grid grid-cols-2 gap-4 mt-4">
                <button
                  mat-flat-button
                  color="primary"
                  type="button"
                  class="w-full p-6"
                  (click)="openCableDiameterCalculator()"
                >
                  CABLE DIAMETER CALCULATOR
                </button>
                <button
                  mat-flat-button
                  color="primary"
                  type="button"
                  class="w-full p-6"
                  (click)="openCompositeCableCalculator()"
                >
                  COMPOSITE CABLE CALCULATOR
                </button>
              </div>
            </div>
          </div>

          @if (
            constructionSheet()?.length > 0 &&
            !(quoteForm.get('quoteType')?.value === 'customQuote')
          ) {
            <div class="grid grid-cols-1 gap-6 mb-8">
              <ciwc-construction-text
                [constructionSheet]="[constructionSheet()]"
                [isConstructionSheet]="true"
                [description]="constructionText()"
              ></ciwc-construction-text>
            </div>

            @if (processedConstructionSteps().length) {
              <mat-accordion>
                <mat-expansion-panel #panel>
                  <mat-expansion-panel-header>
                    <mat-panel-title>
                      {{
                        panel.expanded
                          ? 'Hide All Construction Steps'
                          : 'View All Construction Steps'
                      }}
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  <ciwc-construction-text
                    [constructionSheet]="processedConstructionSteps()"
                    [description]="remainingDescriptions()"
                  ></ciwc-construction-text>
                </mat-expansion-panel>
              </mat-accordion>
            }
          }

          <div class="mt-4">
            <h2 class="text-lg font-medium pb-2">Quote Totals:</h2>
            @for (break of quantityBreaksArray.controls; track break; let i = $index) {
              <ciwc-quote-total-tabs [tabs]="getQuoteTotalTabsForBreak(i)"> </ciwc-quote-total-tabs>
            }
          </div>
          <div class="acordeon-container p-4 pt-0 border border-gray-200 h-full">
            <div class="flex justify-between items-center py-4">
              <h2 class="text-lg font-medium pl-2">Quote Details:</h2>
              <button
                mat-mini-fab
                color="primary"
                type="button"
                (click)="onAddItemProcessToQuoteClick()"
              >
                <mat-icon>add</mat-icon>
              </button>
            </div>
            @for (accordion of quoteDetailAccordions(); let i = $index; track i) {
              <ciwc-quote-detail-accordion
                [title]="accordion.title"
                [key]="accordion.key"
                [color]="accordion.color"
                [data]="accordion.data"
                [quantityBreaks]="quantityBreaksArray.value"
                (deleteItem)="onDeleteQuoteDetail($event)"
                (updateItem)="onAccordionUpdate($event)"
                (fieldUpdate)="onFieldUpdate($event)"
                (dataSourceChange)="onAccordionDataSourceChange($event)"
              >
              </ciwc-quote-detail-accordion>
            }
          </div>
          <div class="mt-8 grid grid-cols-2 gap-6">
            <div>
              <div class="bg-blue-500 text-white p-3 mb-2 h-12">
                <h2 class="text-lg font-medium">Add Note</h2>
              </div>
              <div>
                @if (displayNotes().length) {
                  <div class="mt-4 p-4">
                    @for (note of displayNotes(); let i = $index; track i) {
                      <div class="mb-4 border-b-2 border-gray-200">
                        <p class="text-lg font-medium">{{ note.note }}</p>
                        <p class="text-right text-gray-600">
                          - {{ note.createdBy.firstName }} {{ note.createdBy.lastName }},
                          {{ note.dateCreated | date: CONFIG.date.formatWithTime }}
                        </p>
                      </div>
                    }
                  </div>
                }
                <mat-form-field class="w-full">
                  <mat-label>Note</mat-label>
                  <input matInput formControlName="notes" />
                </mat-form-field>
                <div class="flex justify-end">
                  <button mat-flat-button color="primary" type="button" (click)="saveNote()">
                    SAVE NOTE
                  </button>
                </div>
              </div>
            </div>

            @if (isEditMode()) {
              <div>
                <div class="bg-blue-500 text-white p-3 mb-2 h-12">
                  <h2 class="text-lg font-medium">Edit History</h2>
                </div>
                <div class="p-4">
                  @for (log of selectedQuote()?.historyLogs; let i = $index; track i) {
                    <p class="mb-4 border-b-2">
                      {{ log.createdBy?.firstName }} {{ log.createdBy?.lastName }}
                      {{ log.description }} on
                      {{ log.dateCreated | date: CONFIG.date.formatWithTime }}
                    </p>
                  }
                </div>
              </div>
            }
          </div>
        </form>
      </mat-card-content>

      <mat-card-actions class="flex justify-end gap-[10px]">
        <button
          mat-flat-button
          (click)="goBack()"
          [disabled]="createdQuoteLoading() || updatedQuoteLoading()"
        >
          CANCEL
        </button>
        <button
          mat-flat-button
          color="accent"
          (click)="onSubmit()"
          [disabled]="createdQuoteLoading() || updatedQuoteLoading()"
        >
          {{ isEditMode() ? 'SAVE CHANGES' : 'ADD QUOTE' }}
        </button>
      </mat-card-actions>
    }
  </mat-card>
</div>
